var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ToolkitConstants, Errors } from "../toolkit.config";
import { getContentConfig, getErrorMessage, getServerConfig, } from "../utils/index";
/**
 * @function generateJsonResponseContentBaseEndpoint
 * @description **generates the base JSON endpoint for the content and media.**
 * - this is used to construct base path with language and preview options.
 * - en is the default language.
 * @param {string} contentTypeMachineName - machine name of the content type.
 * @param {object} [optionObject] `optional` object with optional parameters.
 * @param {string} [optionObject.lang] `optional` language of the content.
 * @param {boolean} [optionObject.isPreview] `optional` preview flag.
 * @param {string} [optionObject.previewId] `optional` preview ID of the content.
 * @param {string} [optionObject.previewerId] `optional` user ID of the previewer.
 * @param {object} [optionObject.nodeFields] `optional` object with fields.
 * @param {object} [optionObject.paragraphFields] `optional` object with fields.
 * @param {object} [optionObject.filters] `optional` object with filters.
 * @param {object} [optionObject.pagination]  `optional` object with pagination entities
 * @param {string[]} [optionObject.sorting] `optional` array of sorting parameters
 * @param {number} [optionObject.pagination.offset] - page offset
 * @param {number} [optionObject.pagination.limit] - page limit
 * @returns {Promise<string>} promise of string of the JSON base endpoint which will fetch content including media endpoints.
 */
export const generateJsonResponseContentBaseEndpoint = (contentTypeMachineName, optionObject) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b, _c, _d, _e;
    const { lang, isPreview, previewId, previewerId, nodeFields, paragraphFields, filters, pagination, sorting, } = optionObject;
    if (isPreview && (!previewId || !previewerId)) {
        throw new Error(getErrorMessage(Errors.DTK002005));
    }
    if (pagination && (!(pagination === null || pagination === void 0 ? void 0 : pagination.limit) || !(pagination === null || pagination === void 0 ? void 0 : pagination.offset))) {
        throw new Error(getErrorMessage(Errors.DTK002006));
    }
    const serverConfig = yield getServerConfig();
    const languageCode = (_a = lang === null || lang === void 0 ? void 0 : lang.split("-")[0]) !== null && _a !== void 0 ? _a : "en";
    const langPath = !languageCode || languageCode === "en" ? "" : `/${languageCode}`;
    const nodeFieldParamsUrlSegment = generateFieldParamsUrlSegment(nodeFields, false);
    const paragraphgrapFieldParamsUrlSegment = generateFieldParamsUrlSegment(paragraphFields, true);
    const filterParamsUrlSegment = generateFilterParamsUrlSegment(filters);
    const paginationParamUrlSegment = generatePaginationParams(pagination);
    const sortingParamUrlSegment = generateSortingParams(sorting);
    const generatedSearchParams = (_b = new URLSearchParams(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, nodeFieldParamsUrlSegment), paragraphgrapFieldParamsUrlSegment), filterParamsUrlSegment), paginationParamUrlSegment), sortingParamUrlSegment))) === null || _b === void 0 ? void 0 : _b.toString();
    let contentApiEndpoint;
    const drupalServerUrl = ((_c = serverConfig.DRUPAL_SERVER_URL) === null || _c === void 0 ? void 0 : _c.endsWith("/"))
        ? (_d = serverConfig.DRUPAL_SERVER_URL) === null || _d === void 0 ? void 0 : _d.slice(0, -1)
        : (_e = serverConfig.DRUPAL_SERVER_URL) !== null && _e !== void 0 ? _e : null;
    if (isPreview) {
        contentApiEndpoint = `${drupalServerUrl}/api/preview/${previewId}?_format=json&uid=${previewerId}${languageCode ? "&langcode=" + languageCode : ""}${generatedSearchParams}`;
    }
    else {
        contentApiEndpoint = `${drupalServerUrl}${langPath}/jsonapi/node/${contentTypeMachineName}${generatedSearchParams ? "?" + generatedSearchParams : ""}`;
    }
    return contentApiEndpoint;
});
/**
 * @function generatePaginationParams
 * @description **generates the sorting parameters for the JSON endpoint.**
 * @param {string[]} paramObject - array of sorting parameters
 * @returns {string} string of sorting parameters
 */
export const generateSortingParams = (paramObject) => {
    let sortingParams = {};
    if (!paramObject)
        return sortingParams;
    sortingParams = { sort: paramObject.join(",") };
    return sortingParams;
};
/**
 * @function generatePaginationParams
 * @description **generates the pagination parameters for the JSON endpoint.**
 * @param {object} paramObject - pagination parameter object
 * @returns {string} string of pagination parameters
 */
export const generatePaginationParams = (paramObject) => {
    var _a, _b;
    let paginationParams = {};
    if (!paramObject)
        return paginationParams;
    paginationParams = {
        "page[limit]": (_a = paramObject === null || paramObject === void 0 ? void 0 : paramObject.limit) === null || _a === void 0 ? void 0 : _a.toString(),
        "page[offset]": (_b = paramObject === null || paramObject === void 0 ? void 0 : paramObject.offset) === null || _b === void 0 ? void 0 : _b.toString(),
    };
    return paginationParams;
};
/**
 * @function generateFilterParamsUrlSegment
 * @description **generates the filter parameters for the JSON endpoint.**
 * @param {object} paramObject - filter parameter object
 * @returns {string} string of filter parameters
 */
export const generateFilterParamsUrlSegment = (paramObject) => {
    var _a;
    const filterParams = {};
    if (!paramObject)
        return filterParams;
    (_a = Object.entries(paramObject)) === null || _a === void 0 ? void 0 : _a.forEach(([type, filter]) => {
        filterParams[`filter[${type}]`] = filter;
    });
    return filterParams;
};
/**
 * @function generateFieldParamsUrlSegment
 * @description **generates the field parameters for the JSON endpoint.**
 * @param {object} paramObject - field parameter object
 * @param {boolean} isParagraph - identifier for paragraph or node
 * @returns {string} string of field parameters
 */
export const generateFieldParamsUrlSegment = (paramObject, isParagraph) => {
    var _a;
    const fieldParams = {};
    if (!paramObject)
        return fieldParams;
    (_a = Object.entries(paramObject)) === null || _a === void 0 ? void 0 : _a.forEach(([type, fields]) => {
        fieldParams[`fields[${isParagraph ? "paragraph" : "node"}--${type}]`] =
            fields.join(",");
    });
    return fieldParams;
};
/**
 * @function generateJsonResponseContentEndpoint
 * @description **generates the JSON endpoint for the content and media.**
 * - this is used to construct the include parameters for the JSON endpoint.
 * - the include parameters are used to fetch the content and media in a single request.
 * @param {string} contentTypeMachineName - machine name of the content type.
 * @param {string} contentRefField - machine name of the content reference type paragraph field.
 * @param {Array<ToolkitResponseProcessedInclude>} paragraphsUsedInContent - array of paragraphs used in the content.
 * @param {object} optionObject - object with optional parameters.
 * @returns {Promise<string>} promise of string of the JSON endpoint which will fetch content including media endpoints.
 */
export const generateJsonResponseContentEndpoint = (contentTypeMachineName, contentRefField, paragraphsUsedInContent, optionObject) => __awaiter(void 0, void 0, void 0, function* () {
    var _f;
    const contentConfig = yield getContentConfig();
    const baseEndpoint = yield generateJsonResponseContentBaseEndpoint(contentTypeMachineName, optionObject);
    const isQueryParamsExist = baseEndpoint.includes("?");
    let contentJsonEndpoint = `${baseEndpoint}${isQueryParamsExist ? "&" : "?"}include=`;
    let includeParams = "";
    const individualIncludeParams = [];
    for (const paragraphType of paragraphsUsedInContent) {
        const cleanedParagraphType = ((_f = paragraphType === null || paragraphType === void 0 ? void 0 : paragraphType.type) === null || _f === void 0 ? void 0 : _f.split("--")[1]) || paragraphType.type;
        const paragraphConfig = contentConfig.paragraphs[cleanedParagraphType];
        if (!paragraphConfig)
            continue;
        let fieldSpecificIncludeParam = "";
        for (const machineName of paragraphConfig.machineNames) {
            let audioParam = "";
            let videoParam = "";
            let imageParam = "";
            let documentParam = "";
            if (machineName.isMediaType) {
                audioParam += `${contentRefField}.${machineName.machineName}.${ToolkitConstants.MACHINENAME_AUDIO}`;
                videoParam += `${contentRefField}.${machineName.machineName}.${ToolkitConstants.MACHINENAME_VIDEO}`;
                imageParam += `${contentRefField}.${machineName.machineName}.${ToolkitConstants.MACHINENAME_IMAGE}`;
                documentParam += `${contentRefField}.${machineName.machineName}.${ToolkitConstants.MACHINENAME_DOCUMENT}`;
                fieldSpecificIncludeParam = [
                    audioParam,
                    videoParam,
                    imageParam,
                    documentParam,
                ].join(",");
            }
        }
        if (fieldSpecificIncludeParam.length > 0) {
            individualIncludeParams.push(fieldSpecificIncludeParam);
        }
    }
    includeParams = individualIncludeParams.join(",");
    if (!includeParams.length) {
        includeParams = `${contentRefField !== null && contentRefField !== void 0 ? contentRefField : ""}`;
    }
    contentJsonEndpoint += encodeURIComponent(includeParams);
    return contentJsonEndpoint;
});
/**
 * @function generateJsonMenuEndpoint
 * @description **generates the JSON endpoint for the media.**
 * - this is used to construct the menu end point.
 * - en is default language.
 * @param {string} menuMachineName - machine name of the menu.
 * @param {string} [lang] `optional` language of the content.
 * @returns {Promise<string>} promise of string of the JSON endpoint which will fetch media endpoints.
 */
export const generateJsonMenuEndpoint = (menuMachineName, lang) => __awaiter(void 0, void 0, void 0, function* () {
    var _g, _h, _j, _k, _l;
    const serverConfig = yield getServerConfig();
    const drupalMenuUrl = ((_g = serverConfig.DRUPAL_SERVER_URL_MENU) === null || _g === void 0 ? void 0 : _g.endsWith("/"))
        ? (_h = serverConfig.DRUPAL_SERVER_URL_MENU) === null || _h === void 0 ? void 0 : _h.slice(0, -1)
        : serverConfig.DRUPAL_SERVER_URL_MENU;
    const drupalServerUrl = ((_j = serverConfig.DRUPAL_SERVER_URL) === null || _j === void 0 ? void 0 : _j.endsWith("/"))
        ? (_k = serverConfig.DRUPAL_SERVER_URL) === null || _k === void 0 ? void 0 : _k.slice(0, -1)
        : serverConfig.DRUPAL_SERVER_URL;
    const languageCode = (_l = lang === null || lang === void 0 ? void 0 : lang.split("-")[0]) !== null && _l !== void 0 ? _l : "en";
    const langPath = !languageCode || languageCode === "en" ? "" : `/${languageCode}`;
    const menuApiEndpoint = `${drupalMenuUrl !== null && drupalMenuUrl !== void 0 ? drupalMenuUrl : drupalServerUrl}${langPath}/system/menu/${menuMachineName}/linkset`;
    return menuApiEndpoint;
});
